import type { Key, ReactElement, ReactNode } from 'react';
import type { TableProps as BaseTableProps } from 'antd';
import type { ColumnProps as BaseColumnProps, ColumnType as BaseColumnType } from 'antd/es/table';
import type { MenuDividerType, MenuItemType } from 'antd/es/menu/interface';
import type { SorterResult as BaseSorterResult } from 'antd/es/table/interface';
import type { Column, Row } from 'exceljs';

export interface TableRef {
  setHighlightKey: (key: Key) => void;
}

export type ColumnProps<T> = BaseColumnProps<T> & {
  show?: boolean;
};

export interface TableProps<T> {
  dataSource?: BaseTableProps<T>['dataSource'];
  children?: ReactElement<ColumnProps<T>>[];
  columns?: BaseTableProps<T>['columns'];
  rowKey?: BaseTableProps<T>['rowKey'];
  pagination?: BaseTableProps<T>['pagination'];
  virtual?: BaseTableProps<T>['virtual'];
  summary?: BaseTableProps<T>['summary'];
  /**
   * 分页数据总数
   */
  current?: number;
  /**
   * 分页数据总数
   */
  total?: number;
  /**
   * 分页一页数据量
   */
  pageSize?: number;
  /**
   * 切换分页
   */
  onPageChange?: (nextPage: number, pageSize: number) => void;
  /**
   * 滚动区域（指定列的 fixed:left 属性后，能实现固定列）
   */
  scroll?: BaseTableProps<T>['scroll'];
  /**
   * class name
   */
  className?: string;
  /**
   * 加载中
   */
  loading?: boolean;
  /**
   * 边框
   */
  bordered?: boolean;
  /**
   * 表格大小
   */
  size?: BaseTableProps<T>['size'];
  locale?: BaseTableProps<T>['locale'];
  /**
   * 表格行是否可选择
   */
  rowSelection?: BaseTableProps<T>['rowSelection'];
  rowClassName?: BaseTableProps<T>['rowClassName'];
  onRow?: BaseTableProps<T>['onRow'];
  rowHighlight?: {
    key?: string | number;
  };
  /*
   * 移动端
   */
  mobile?: boolean;
  emptyText?: ReactNode;
  onChange?: BaseTableProps<T>['onChange'];
}

export interface NoProps<T> extends ColumnProps<T> {
}

export interface TextProps<T> extends ColumnProps<T> {
}

export interface InputOption<T> {
  /**
   * 返回当前数据的行索引
   */
  index: number;

  /**
   * 返回当前数据的列索引（dataIndex值）
   */
  dataIndex: ColumnProps<T>['dataIndex'];
}

export interface InputProps<T> extends ColumnProps<T> {
  placeholder?: string;
  status?: 'error' | 'warning';
  type?: 'number' | 'text';
  onChange?: (value: string, record: T, option: InputOption<T>) => void;
}

export interface MappingProps<T> extends ColumnProps<T> {
  mapping: Record<string, ReactNode>;
}

export interface DayProps<T> extends ColumnProps<T> {
  format?:
  'YYYY-MM-DD' |
  'YYYY-MM-DD HH:mm' |
  'YYYY-MM-DD HH:mm:ss' |
  'MM-DD' |
  'HH:mm';
}

export interface ActionProps<T> extends ColumnProps<T> {
  /**
   * 操作按钮名称
   */
  label: string;

  /**
   * 下拉菜单按钮
   */
  menu?: ActionMenuProps<T>;

  /**
   * 操作按钮事件
   */
  onClick: (record: T) => void;

  /**
   * 操作按钮样式
   */
  align?: 'left' | 'right' | 'center';

  /**
   * 操作按钮图标
   */
  labelIcon?: ReactNode;

  /**
   * 操作按钮固定位置
   */
  fixed?: 'left' | 'right';
  /**
   * 下拉菜单中操作按钮是否禁用
   */
  menuItemDisabled?: (record: T) => boolean;
  /**
   * 下拉菜单中操作按钮禁用的key
   */
  menuItemDisabledKey?: string;
}

export enum TableActionTypeEnum {
  BUTTON = 'button',
  DROP_DOWN = 'dropDown'
}

type MenuItem<RecordType> = Pick<MenuItemType, 'label' | 'key' | 'icon' | 'disabled' | 'danger'> & ActionClickHandler<RecordType>;
type MenuDivider = MenuDividerType;
interface ActionClickHandler<RecordType> {
  onClick?: (record: RecordType) => void;
}

export interface ActionMenuProps<RecordType> {
  items: ((MenuItem<RecordType>) | MenuDivider)[];
}

export function isActionMenuItem<RecordType>(item: MenuItem<RecordType> | MenuDivider): item is MenuItem<RecordType> {
  return (item as MenuItem<RecordType>).key != null;
}

/**
 * 支持的组件列表
 */
export type Components = 'No' | 'Day' | 'Mapping' | 'Text' | 'Input' | 'Action' | 'Customize';

export interface DownloadParams<T> {
  fileName: string;
  sheets: SheetType<T>[];
}

export type ExcelColumns = Partial<Column>[];

export interface Sheet<T> {
  sheetName?: string;
  columns: ColumnType<T>[];
  dataSource: T[];
  manual?: boolean;
  custom?: false;
  eachRow?: (row: Row, rowNumber: number) => void;
}

export type ColumnType<T> = BaseColumnType<T> & {
  numFmt?: string; // 单元格格式化
};

export interface CustomSheet {
  manual?: boolean;
  /** 表名 默认值等于文件名 */
  sheetName?: string;
  /** execljs的列定义 */
  columns: ExcelColumns;
  /** 是否开启自定义导出表格 */
  custom: true;
  /** 自定义导出表格行数据 */
  rows: Record<string, string | number>[];
  /**
   * 合并单元格配置项
   * @example ['A1:A2','B1:C1'] A1和A2单元格合并，B1和C1单元格合并
   */
  mergeCellOptions?: string[];
  eachRow?: (row: Row, rowNumber: number) => void;
}

export type SheetType<T> = Sheet<T> | CustomSheet;

export interface CustomizeProps<T = unknown> extends ColumnProps<T> {
  show?: boolean;
  render: (value: string, record?: T) => ReactNode;
}

export type DynamicValue = Record<string, unknown>;

export type TransformTableData = Record<string, DynamicValue>;

export type TableSort<RecordType> = SorterResult<RecordType> | SorterResult<RecordType>[];

export type SorterResult<RecordType> = BaseSorterResult<RecordType>;
